<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">流程管理</h1>
            <p class="list-page-description">流程设计、发布与管理。</p>
          </div>
          <div class="list-page-heading-actions">
            <button
              v-permission="'workflow:template:add'"
              @click="createWorkflow"
              class="btn-primary"
            >
              <el-icon><Plus /></el-icon>
              新建流程
            </button>
          </div>
        </div>
      </div>
    </template>
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <!-- 左侧类目管理 -->
          <div class="lg:col-span-3">
            <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div class="flex items-center justify-between">
                <h2 class="text-base font-semibold text-slate-900">流程分类管理</h2>
                <button
                  v-permission="'workflow:category:add'"
                  @click="openCategoryModal()"
                  class="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                >
                  新增分类
                </button>
              </div>
              <p class="mt-2 text-xs text-slate-500">删除前会校验，分类下存在流程将无法删除。</p>

              <div v-if="categoryLoading" class="mt-4 space-y-3">
                <div v-for="n in 3" :key="n" class="h-16 rounded-xl border border-slate-200 bg-slate-50"></div>
              </div>

              <div v-else class="mt-4 space-y-3">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  @click="applyCategoryFilter(category.id)"
                  :class="[
                    'flex cursor-pointer items-center justify-between rounded-xl border p-4 transition',
                    selectedCategory && Number(selectedCategory) === category.id
                      ? 'border-primary-300 bg-primary-50/70 ring-1 ring-primary-200'
                      : 'border-slate-200 bg-slate-50/70 hover:bg-slate-50'
                  ]"
                >
                  <div class="flex items-center gap-3">
                    <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                      <el-icon :size="20" class="text-primary-600">
                        <Folder />
                      </el-icon>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-slate-900">{{ category.categoryName }}</div>
                      <div class="mt-1 flex items-center gap-2 text-xs text-slate-500">
                        <span>{{ category.categoryCode }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      v-permission="'workflow:category:edit'"
                      @click.stop="openCategoryModal(category)"
                      class="p-1.5 text-slate-400 hover:text-primary-600 transition-colors"
                      title="编辑"
                    >
                      <el-icon><Edit /></el-icon>
                    </button>
                    <button
                      v-permission="'workflow:category:delete'"
                      @click.stop="confirmDeleteCategory(category)"
                      class="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      title="删除"
                    >
                      <el-icon><Delete /></el-icon>
                    </button>
                  </div>
                </div>

                <div v-if="!categories.length" class="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                  暂无分类数据
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧流程列表 -->
          <div class="lg:col-span-9">
            <div class="list-page-table-panel">
              <div class="border-b border-slate-200 p-5">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 class="text-base font-semibold text-slate-900">流程列表</h2>
                    <p class="mt-1 text-xs text-slate-500">点击流程名称可查看流程图与节点配置。</p>
                  </div>
                </div>
                <PageFilterBar
                  shell-class="filter-bar-shell is-embedded"
                  body-class="filter-bar-body is-embedded"
                  :show-refresh="true"
                  :refresh-loading="loading"
                  @refresh="handleRefresh"
                >
                  <div class="filter-bar-field is-wide relative">
                    <input
                      v-model="queryParams.templateName"
                      type="text"
                      placeholder="搜索流程名称"
                      class="form-search-input form-control-sm w-full"
                      @keyup.enter="handleQuery"
                    >
                    <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  </div>
                  <el-select v-model="queryParams.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
                    <el-option label="全部状态" value="" />
                    <el-option label="已启用" :value="1" />
                    <el-option label="已停用" :value="0" />
                  </el-select>

                  <template #searchActions>
                    <button
                      type="button"
                      class="btn-primary"
                      :disabled="loading"
                      @click="handleQuery"
                    >
                      搜索
                    </button>
                    <button
                      type="button"
                      class="btn-secondary"
                      :disabled="loading"
                      @click="resetQuery"
                    >
                      重置
                    </button>
                  </template>
                </PageFilterBar>
              </div>

              <el-table
                class="app-table"
                :data="tableData"
                v-loading="loading"
                style="width: 100%"
              >
                <el-table-column prop="templateName" label="流程名称" min-width="200">
                  <template #default="{ row }">
                    <div class="flex items-center gap-2">
                      <el-icon :size="16" :color="getStatusColor(row.status)">
                        <Connection />
                      </el-icon>
                      <span class="font-medium">{{ row.templateName }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="categoryCode" label="分类" width="120" align="center">
                  <template #default="{ row }">
                    <span class="data-table-inline-tag">{{ row.categoryCode || '未分类' }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="status" label="状态" width="100" align="center">
                  <template #default="{ row }">
                    <span :class="['status-badge', workflowStatusClass(row.status)]">
                      {{ statusLabel(row.status) }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="version" label="版本" width="80" align="center">
                  <template #default="{ row }">
                    <span class="data-table-inline-tag">v{{ row.version }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="nodeCount" label="节点数" width="90" align="center">
                  <template #default="{ row }">
                    <span class="text-slate-600">{{ row.nodeCount || 0 }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
                <el-table-column prop="createTime" label="创建时间" width="160" align="center">
                  <template #default="{ row }">
                    <span class="text-slate-600 text-xs">{{ row.createTime }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="180" align="center" fixed="right">
                  <template #default="{ row }">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="viewWorkflow(row)" class="table-action-button is-primary" title="查看详情" aria-label="查看详情">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </button>
                      <button v-permission="'workflow:template:edit'" @click="editWorkflow(row)" class="table-action-button is-primary" title="编辑" aria-label="编辑">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                      <button v-if="row.status === 0" v-permission="'workflow:template:edit'" @click="handlePublish(row)" class="table-action-button is-success" title="启用" aria-label="启用">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </button>
                      <button v-if="row.status === 1" v-permission="'workflow:template:edit'" @click="handleDisable(row)" class="table-action-button is-neutral" title="停用" aria-label="停用">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </button>
                      <button v-permission="'workflow:template:delete'" @click="handleDelete(row)" class="table-action-button is-danger" title="删除" aria-label="删除">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>

              <!-- 分页 -->
              <AppPagination
                v-if="total > 0"
                class="pagination-bridge"
                :current="queryParams.pageNum"
                :size="queryParams.pageSize"
                :total="total"
                :pages="totalPages"
                :page-sizes="[10, 20, 50, 100]"
                @page-change="handlePageChange"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>


    <!-- 分类管理弹窗 -->
    <div v-if="showCategoryModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/50" @click="closeCategoryModal"></div>
      <div class="relative mx-auto flex min-h-screen w-full max-w-md items-center px-6 py-8">
        <div class="w-full rounded-2xl bg-white shadow-xl">
          <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h3 class="text-lg font-semibold text-slate-900">{{ isCategoryEdit ? '编辑流程分类' : '新增流程分类' }}</h3>
            <button @click="closeCategoryModal" class="rounded-full p-1 text-slate-400 hover:text-slate-600">
              <el-icon :size="20"><Close /></el-icon>
            </button>
          </div>
          <div class="px-6 py-5">
            <div class="space-y-4">
              <div>
                <label class="text-sm font-medium text-slate-700">分类编码 <span class="text-red-500">*</span></label>
                <input
                  v-model="categoryForm.categoryCode"
                  :disabled="isCategoryEdit"
                  class="form-input mt-2 w-full"
                  placeholder="例如 WORKFLOW_ABSENCE"
                >
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">分类名称 <span class="text-red-500">*</span></label>
                <input
                  v-model="categoryForm.categoryName"
                  class="form-input mt-2 w-full"
                  placeholder="例如 请假流程"
                >
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">排序</label>
                <input
                  v-model.number="categoryForm.sort"
                  type="number"
                  class="form-input mt-2 w-full"
                >
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">描述</label>
                <textarea
                  v-model="categoryForm.description"
                  rows="3"
                  class="form-textarea mt-2"
                  placeholder="分类说明"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <button @click="closeCategoryModal" class="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">取消</button>
            <button @click="submitCategory" class="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700">保存</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/50" @click="closeDeleteModal"></div>
      <div class="relative mx-auto flex min-h-screen max-w-md items-center px-6 py-8">
        <div class="w-full rounded-2xl bg-white shadow-xl">
          <div class="border-b border-slate-200 px-6 py-4">
            <h3 class="text-lg font-semibold text-slate-900">确认删除</h3>
          </div>
          <div class="px-6 py-5 text-sm text-slate-600">
            {{ deleteConfirmMessage }}
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <button @click="closeDeleteModal" class="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">取消</button>
            <button @click="confirmDeleteAction" class="rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600">删除</button>
          </div>
        </div>
      </div>
    </div>

  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Plus,
  Edit,
  Close,
  Delete,
  Connection,
  Folder
} from '@element-plus/icons-vue'
import {
  getTemplatePage,
  deleteTemplate,
  updateTemplateStatus,
  getWorkflowCategories,
  createWorkflowCategory,
  updateWorkflowCategory,
  deleteWorkflowCategory
} from '@/api'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

const router = useRouter()

// 查询参数
const queryParams = reactive({
  templateName: '',
  status: '',
  categoryCode: '',
  pageNum: 1,
  pageSize: 10
})

// 表格数据
const tableData = ref([])
const loading = ref(false)
const total = ref(0)
const totalPages = computed(() => Math.ceil(total.value / queryParams.pageSize) || 1)

// 分类列表
const categories = ref([])
const categoryLoading = ref(false)
const selectedCategory = ref('')

// 分类弹窗
const showCategoryModal = ref(false)
const isCategoryEdit = ref(false)
const categoryForm = reactive({
  id: null,
  categoryCode: '',
  categoryName: '',
  sort: 1,
  description: ''
})

// 删除确认弹窗
const showDeleteModal = ref(false)
const deleteConfirmMessage = ref('')
const deletePayload = ref({ type: '', id: null })

function statusLabel(status) {
  const labels = {
    0: '已停用',
    1: '已启用'
  }
  return labels[status] || status
}

function workflowStatusClass(status) {
  const classes = {
    0: 'bg-rose-100 text-rose-700',
    1: 'bg-emerald-100 text-emerald-700'
  }
  return classes[status] || 'bg-slate-100 text-slate-600'
}

function getStatusColor(status) {
  const colors = {
    0: '#f56c6c',
    1: '#67c23a'
  }
  return colors[status] || '#909399'
}

// 查询
async function handleQuery() {
  queryParams.pageNum = 1
  await fetchData()
}

// 重置
function resetQuery() {
  queryParams.templateName = ''
  queryParams.status = ''
  queryParams.categoryCode = selectedCategory.value || ''
  queryParams.pageNum = 1
  fetchData()
}

function handleRefresh() {
  fetchData()
}

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const data = await getTemplatePage(queryParams)
    tableData.value = data.records || []
    total.value = data.total || 0
  } catch (error) {
    console.error('获取流程列表失败:', error)
    ElMessage.error(error.message || '获取流程列表失败')
  } finally {
    loading.value = false
  }
}

// 获取分类列表
async function fetchCategories() {
  categoryLoading.value = true
  try {
    const data = await getWorkflowCategories()
    categories.value = data || []
  } catch (error) {
    console.error('获取分类列表失败:', error)
    ElMessage.error(error.message || '获取分类列表失败')
  } finally {
    categoryLoading.value = false
  }
}

// 应用分类筛选
async function applyCategoryFilter(categoryId) {
  const nextValue = String(categoryId)
  selectedCategory.value = selectedCategory.value === nextValue ? '' : nextValue
  queryParams.categoryCode = selectedCategory.value
  await fetchData()
}

// 打开分类弹窗
function openCategoryModal(category) {
  if (category) {
    Object.assign(categoryForm, {
      id: category.id,
      categoryCode: category.categoryCode,
      categoryName: category.categoryName,
      sort: category.sort || 1,
      description: category.description || ''
    })
    isCategoryEdit.value = true
  } else {
    Object.assign(categoryForm, {
      id: null,
      categoryCode: '',
      categoryName: '',
      sort: categories.value.length + 1,
      description: ''
    })
    isCategoryEdit.value = false
  }
  showCategoryModal.value = true
}

// 关闭分类弹窗
function closeCategoryModal() {
  showCategoryModal.value = false
}

// 提交分类
async function submitCategory() {
  if (!categoryForm.categoryCode || !categoryForm.categoryName) {
    ElMessage.warning('请填写分类编码和名称')
    return
  }

  try {
    if (isCategoryEdit.value) {
      await updateWorkflowCategory({
        categoryId: categoryForm.id,
        categoryName: categoryForm.categoryName,
        sort: categoryForm.sort,
        description: categoryForm.description
      })
      ElMessage.success('分类更新成功')
    } else {
      await createWorkflowCategory({
        categoryCode: categoryForm.categoryCode,
        categoryName: categoryForm.categoryName,
        sort: categoryForm.sort,
        description: categoryForm.description
      })
      ElMessage.success('分类创建成功')
    }
    showCategoryModal.value = false
    await fetchCategories()
  } catch (error) {
    ElMessage.error(error.message || '保存分类失败')
  }
}

// 确认删除分类
function confirmDeleteCategory(category) {
  deletePayload.value = { type: 'category', id: category.id }
  deleteConfirmMessage.value = `确认删除分类「${category.categoryName}」？若分类下存在流程将无法删除。`
  showDeleteModal.value = true
}

// 关闭删除确认弹窗
function closeDeleteModal() {
  showDeleteModal.value = false
}

// 确认删除操作
async function confirmDeleteAction() {
  const { type, id } = deletePayload.value
  try {
    if (type === 'category') {
      await deleteWorkflowCategory({ categoryId: id })
      ElMessage.success('分类已删除')
      // 如果删除的是当前筛选的分类，清除筛选
      if (selectedCategory.value === String(id)) {
        selectedCategory.value = ''
        queryParams.categoryCode = ''
      }
      await fetchCategories()
    }
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    showDeleteModal.value = false
  }
}

// 分页
function handlePageChange(page) {
  queryParams.pageNum = page
  fetchData()
}

function handlePageSizeChange(size) {
  queryParams.pageSize = size
  queryParams.pageNum = 1
  fetchData()
}

// 新建流程
function createWorkflow() {
  router.push({ name: 'WorkflowDesigner' })
}

// 查看流程详情
function viewWorkflow(row) {
  router.push({
    name: 'WorkflowDesigner',
    query: { id: row.id, mode: 'view' }
  })
}

// 编辑流程
function editWorkflow(row) {
  router.push({
    name: 'WorkflowDesigner',
    query: { id: row.id }
  })
}

// 发布流程（更新状态为启用）
async function handlePublish(row) {
  try {
    await ElMessageBox.confirm(
      `确定要启用流程"${row.templateName}"吗？启用后用户即可使用该流程。`,
      '启用确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await updateTemplateStatus({ templateId: row.id, status: 1 })
    ElMessage.success('启用成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('启用失败:', error)
      ElMessage.error(error.message || '启用失败')
    }
  }
}

// 停用流程（更新状态为停用）
async function handleDisable(row) {
  try {
    await ElMessageBox.confirm(
      `确定要停用流程"${row.templateName}"吗？停用后用户将无法发起该流程。`,
      '停用确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await updateTemplateStatus({ templateId: row.id, status: 0 })
    ElMessage.success('停用成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('停用失败:', error)
      ElMessage.error(error.message || '停用失败')
    }
  }
}

// 删除流程
async function handleDelete(row) {
  try {
    await ElMessageBox.confirm(
      `确定要删除流程"${row.templateName}"吗？删除后无法恢复。`,
      '删除警告',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'error'
      }
    )
    await deleteTemplate({ templateId: row.id })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error)
      ElMessage.error(error.message || '删除失败')
    }
  }
}

// 初始化
onMounted(async () => {
  await Promise.all([
    fetchData(),
    fetchCategories()
  ])
})
</script>

<style scoped>
</style>
